<template>
  <div class="operate-page" v-loading="loading">
    <el-form size="small" inline>
      <el-form-item label="统计时间">
        <el-date-picker v-model="dateRange" type="daterange" value-format="yyyy-MM-dd" unlink-panels range-separator="至"
          start-placeholder="开始日期" end-placeholder="结束日期" :clearable="false" />
      </el-form-item>
      <el-form-item label="汇总">
        <el-radio-group v-model="grain">
          <el-radio-button label="month">按月</el-radio-button>
          <el-radio-button label="year">按年</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="query">查询</el-button>
      </el-form-item>
      <el-form-item>
        <ai-insight ref="insight" />
      </el-form-item>
    </el-form>

    <p class="tip">固定电车车队。上座率按实际人数加权。流水 = 合计流水 − 合计补贴。毛收入 = 流水 − 浮动成本 − 固定成本 − 分流费用 − 奖励金额 −
      超额提成。车租、工资和超额提成按自然月核算后再相加。金额超过 1 万时用「万」显示。</p>

    <div class="cards">
      <el-card shadow="never" class="card">
        <div class="num">{{ formatAmount(summary.realIncome) }}</div>
        <div class="label">流水</div>
        <div class="sub">合计流水 − 合计补贴</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatRate(summary.actAttendance) }}</div>
        <div class="label">实际上座率</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatAmount(summary.variableCost) }}</div>
        <div class="label">浮动成本</div>
        <div class="sub">电费 + 过路费</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatAmount(summary.fixedCost) }}</div>
        <div class="label">固定成本</div>
        <div class="sub">车租 + 工资</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ summary.driverCount || 0 }}</div>
        <div class="label">司机数量</div>
        <div class="sub">有成本或出车</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatAmount(summary.shuntFee) }}</div>
        <div class="label">分流成本</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatAmount(summary.awardAmount) }}</div>
        <div class="label">奖金成本</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatAmount(summary.extraPointsCommission) }}</div>
        <div class="label">超额提成</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num" :class="{ minus: Number(summary.pureIncome) < 0 }">{{ formatAmount(summary.pureIncome) }}</div>
        <div class="label">毛收入</div>
        <div class="sub">单边 {{ formatAmount(summary.purePerRoute) }}</div>
      </el-card>
    </div>
    <p class="tip">单人变动成本 {{ formatAmount(summary.variablePerPassenger) }}，按实际人数分摊电费和过路费。</p>

    <div class="block-title">核心指标</div>
    <chart ref="trendChart" :height="380" />

    <div class="block-title">成本构成</div>
    <chart ref="costChart" :height="280" />

    <div class="block-title">固定成本</div>
    <p class="tip">司机数量只计有车租、工资或出车的人。人均固定成本 =（车租 + 工资）/ 司机数，单边固定成本 =（车租 + 工资）/ 边数。积分是行程积分，保底积分是各月合同保底相加后再除以司机数。按月看就是该月；跨月时人均会随月数变大。超额提成仍按单月积分超过该月保底的部分计算。</p>
    <div class="cards fixed-cards">
      <el-card shadow="never" class="card">
        <div class="num">{{ summary.driverCount || 0 }}</div>
        <div class="label">司机数量</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatAmount(summary.fixedPerDriver) }}</div>
        <div class="label">人均固定成本</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatAmount(summary.fixedPerRoute) }}</div>
        <div class="label">单边固定成本</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatPoints(summary.routePoints) }}</div>
        <div class="label">行程积分</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatPoints(summary.pointsPerDriver) }}</div>
        <div class="label">人均积分</div>
      </el-card>
      <el-card shadow="never" class="card">
        <div class="num">{{ formatPoints(summary.basePointsPerDriver) }}</div>
        <div class="label">人均保底积分</div>
      </el-card>
    </div>
    <chart ref="fixedChart" :height="340" />

    <div class="block-title">司机明细</div>
    <el-table :data="drivers" stripe max-height="520" show-summary :summary-method="getSummaries"
      empty-text="该时段没有司机结算">
      <el-table-column prop="driverName" label="姓名" width="100" fixed />
      <el-table-column prop="driverNo" label="编号" width="90" />
      <el-table-column prop="routeNum" label="边数" width="90" sortable />
      <el-table-column prop="actAttendance" label="实际上座率" width="120" sortable>
        <template slot-scope="scope">{{ scope.row.actAttendance }}%</template>
      </el-table-column>
      <el-table-column prop="realIncome" label="流水" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.realIncome) }}</template>
      </el-table-column>
      <el-table-column prop="powerFee" label="电费" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.powerFee) }}</template>
      </el-table-column>
      <el-table-column prop="tollFee" label="过路费" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.tollFee) }}</template>
      </el-table-column>
      <el-table-column prop="carRentCost" label="车租" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.carRentCost) }}</template>
      </el-table-column>
      <el-table-column prop="baseSalaryCost" label="工资" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.baseSalaryCost) }}</template>
      </el-table-column>
      <el-table-column prop="fixedCost" label="固定成本" min-width="110" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.fixedCost) }}</template>
      </el-table-column>
      <el-table-column prop="fixedPerRoute" label="单边固定成本" min-width="130" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.fixedPerRoute) }}</template>
      </el-table-column>
      <el-table-column prop="routePoints" label="积分" min-width="100" sortable>
        <template slot-scope="scope">{{ formatPoints(scope.row.routePoints) }}</template>
      </el-table-column>
      <el-table-column prop="basePoints" label="保底积分" min-width="110" sortable>
        <template slot-scope="scope">{{ formatPoints(scope.row.basePoints) }}</template>
      </el-table-column>
      <el-table-column prop="shuntFee" label="分流" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.shuntFee) }}</template>
      </el-table-column>
      <el-table-column prop="awardAmount" label="奖金" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.awardAmount) }}</template>
      </el-table-column>
      <el-table-column prop="extraPointsCommission" label="超额提成" min-width="110" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.extraPointsCommission) }}</template>
      </el-table-column>
      <el-table-column prop="prueCommissionAmount" label="毛收入" min-width="110" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.prueCommissionAmount) }}</template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { getTeamOperateAnalysis } from "@/api/data";
import { DateUtil } from "@/util";
import AiInsight from "@/components/AiInsight";

function defaultDateRange() {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth() - 5, 1);
  return [DateUtil.format(start, "yyyy-MM-dd"), DateUtil.format(end, "yyyy-MM-dd")];
}

function yuan(value) {
  return Math.round(Number(value || 0)) / 100;
}

function formatYuan(value) {
  const number = Number(value || 0);
  const sign = number < 0 ? "-" : "";
  const abs = Math.abs(number);
  if (abs >= 10000) {
    return `${sign}${(abs / 10000).toFixed(2)}万`;
  }
  return sign + abs.toLocaleString("zh-CN", { maximumFractionDigits: 0 });
}

function costOf(row) {
  return Number(row.variableCost || 0) + Number(row.fixedCost || 0);
}

function highlightDrivers(drivers) {
  const list = drivers || [];
  const lowMargin = list.slice().sort((a, b) => Number(a.prueCommissionAmount || 0) - Number(b.prueCommissionAmount || 0)).slice(0, 3);
  const highCost = list.slice().sort((a, b) => costOf(b) - costOf(a)).slice(0, 3);
  const seen = {};
  const rows = [];
  lowMargin.concat(highCost).forEach((row) => {
    const key = row.driverNo || row.driverName;
    if (seen[key]) {
      return;
    }
    seen[key] = true;
    rows.push({
      driverName: row.driverName,
      driverNo: row.driverNo,
      routeNum: row.routeNum,
      actAttendance: row.actAttendance,
      realIncome: row.realIncome,
      shuntFee: row.shuntFee,
      awardAmount: row.awardAmount,
      extraPointsCommission: row.extraPointsCommission,
      powerFee: row.powerFee,
      tollFee: row.tollFee,
      carRentCost: row.carRentCost,
      baseSalaryCost: row.baseSalaryCost,
      variableCost: row.variableCost,
      fixedCost: row.fixedCost,
      fixedPerRoute: row.fixedPerRoute,
      routePoints: row.routePoints,
      basePoints: row.basePoints,
      prueCommissionAmount: row.prueCommissionAmount
    });
  });
  return rows;
}

export default {
  components: { AiInsight },
  data() {
    return {
      dateRange: defaultDateRange(),
      grain: "month",
      loading: false,
      summary: {},
      drivers: []
    };
  },
  created() {
    this.query();
  },
  methods: {
    formatRate(value) {
      if (value == null || value === "") {
        return "—";
      }
      return `${Number(value).toFixed(1)}%`;
    },
    formatMoney(value, precision) {
      if (value == null || value === "") {
        return "—";
      }
      return (Number(value) / 100).toFixed(precision);
    },
    formatAmount(value) {
      if (value == null || value === "") {
        return "—";
      }
      return formatYuan(yuan(value));
    },
    formatPoints(value) {
      if (value == null || value === "") {
        return "—";
      }
      return Number(value).toLocaleString("zh-CN", { maximumFractionDigits: 2 });
    },
    query() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning("请选择日期");
        return;
      }
      this.loading = true;
      getTeamOperateAnalysis({
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        grain: this.grain
      }).then((res) => {
        const data = res.data || {};
        this.summary = data.summary || {};
        this.drivers = data.drivers || [];
        this.renderTrend(data.trend || []);
        this.renderCost(this.summary);
        this.renderFixed(data.trend || []);
        this.$nextTick(() => {
          if (!this.$refs.insight) {
            return;
          }
          this.$refs.insight.setContext("operate", {
            grain: this.grain,
            dateRange: this.dateRange,
            summary: this.summary,
            trend: data.trend || [],
            drivers: highlightDrivers(this.drivers)
          });
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    renderTrend(trend) {
      const option = {
        tooltip: {
          trigger: "axis",
          formatter(params) {
            const lines = [params[0].axisValue];
            params.forEach((item) => {
              const text = item.seriesName === "上座率" ? `${item.data}%` : formatYuan(item.data);
              lines.push(`${item.marker}${item.seriesName}：${text}`);
            });
            return lines.join("<br/>");
          }
        },
        legend: { data: ["流水", "浮动成本", "固定成本", "分流成本", "奖金成本", "超额提成", "毛收入", "上座率"] },
        grid: { left: 72, right: 56, bottom: 32, top: 72 },
        xAxis: { type: "category", data: trend.map((item) => item.period) },
        yAxis: [
          { type: "value", axisLabel: { formatter: (value) => formatYuan(value) } },
          { type: "value", name: "上座率", axisLabel: { formatter: "{value}%" } }
        ],
        series: [
          { name: "流水", type: "line", data: trend.map((item) => yuan(item.realIncome)) },
          { name: "浮动成本", type: "line", data: trend.map((item) => yuan(item.variableCost)) },
          { name: "固定成本", type: "line", data: trend.map((item) => yuan(item.fixedCost)) },
          { name: "分流成本", type: "line", data: trend.map((item) => yuan(item.shuntFee)) },
          { name: "奖金成本", type: "line", data: trend.map((item) => yuan(item.awardAmount)) },
          { name: "超额提成", type: "line", data: trend.map((item) => yuan(item.extraPointsCommission)) },
          { name: "毛收入", type: "line", data: trend.map((item) => yuan(item.pureIncome)) },
          { name: "上座率", type: "line", yAxisIndex: 1, data: trend.map((item) => item.actAttendance) }
        ]
      };
      this.$nextTick(() => {
        if (this.$refs.trendChart) {
          this.$refs.trendChart.setOption(option, true);
        }
      });
    },
    renderCost(summary) {
      const option = {
        tooltip: {
          trigger: "axis",
          formatter(params) {
            const item = params[0];
            return `${item.name}<br/>${item.marker}${formatYuan(item.data)}`;
          }
        },
        grid: { left: 72, right: 24, bottom: 32, top: 24 },
        xAxis: { type: "category", data: ["电费", "过路费", "车租", "工资", "分流", "奖金", "超额提成"] },
        yAxis: { type: "value", axisLabel: { formatter: (value) => formatYuan(value) } },
        series: [{
          type: "bar",
          barWidth: 36,
          data: [yuan(summary.powerFee), yuan(summary.tollFee), yuan(summary.carRentCost), yuan(summary.baseSalaryCost), yuan(summary.shuntFee), yuan(summary.awardAmount), yuan(summary.extraPointsCommission)]
        }]
      };
      this.$nextTick(() => {
        if (this.$refs.costChart) {
          this.$refs.costChart.setOption(option, true);
        }
      });
    },
    renderFixed(trend) {
      const option = {
        tooltip: {
          trigger: "axis",
          formatter(params) {
            const lines = [params[0].axisValue];
            params.forEach((item) => {
              const text = item.seriesName === "司机数量" ? item.data : formatYuan(item.data);
              lines.push(`${item.marker}${item.seriesName}：${text}`);
            });
            return lines.join("<br/>");
          }
        },
        legend: { data: ["固定成本", "人均固定成本", "司机数量"] },
        grid: { left: 72, right: 56, bottom: 32, top: 48 },
        xAxis: { type: "category", data: trend.map((item) => item.period) },
        yAxis: [
          { type: "value", axisLabel: { formatter: (value) => formatYuan(value) } },
          { type: "value", name: "司机数量", minInterval: 1 }
        ],
        series: [
          { name: "固定成本", type: "bar", barWidth: 28, data: trend.map((item) => yuan(item.fixedCost)) },
          { name: "人均固定成本", type: "line", data: trend.map((item) => yuan(item.fixedPerDriver)) },
          { name: "司机数量", type: "line", yAxisIndex: 1, data: trend.map((item) => item.driverCount || 0) }
        ]
      };
      this.$nextTick(() => {
        if (this.$refs.fixedChart) {
          this.$refs.fixedChart.setOption(option, true);
        }
      });
    },
    getSummaries({ columns, data }) {
      const moneyPrecision = {
        realIncome: 0,
        shuntFee: 2,
        awardAmount: 2,
        extraPointsCommission: 2,
        powerFee: 2,
        tollFee: 2,
        carRentCost: 2,
        baseSalaryCost: 2,
        fixedCost: 2,
        prueCommissionAmount: 2
      };
      return columns.map((column, index) => {
        if (index === 0) {
          return "合计";
        }
        if (column.property === "routeNum") {
          return data.reduce((sum, row) => sum + Number(row.routeNum || 0), 0);
        }
        if (column.property === "fixedPerRoute") {
          const fixed = data.reduce((sum, row) => sum + Number(row.fixedCost || 0), 0);
          const routes = data.reduce((sum, row) => sum + Number(row.routeNum || 0), 0);
          return routes ? this.formatAmount(fixed / routes) : "—";
        }
        if (column.property === "routePoints" || column.property === "basePoints") {
          const total = data.reduce((sum, row) => sum + Number(row[column.property] || 0), 0);
          return this.formatPoints(total);
        }
        if (column.property === "actAttendance") {
          let fullNum = 0;
          let realNum = 0;
          data.forEach((row) => {
            fullNum += Number(row.routeNum || 0) * Number(row.seats || 4);
            realNum += Number(row.num || 0);
          });
          return fullNum ? `${((realNum / fullNum) * 100).toFixed(1)}%` : "—";
        }
        if (Object.prototype.hasOwnProperty.call(moneyPrecision, column.property)) {
          const total = data.reduce((sum, row) => sum + Number(row[column.property] || 0), 0);
          return this.formatAmount(total);
        }
        return "";
      });
    }
  }
};
</script>

<style lang="less" scoped>
.tip {
  color: #909399;
  font-size: 13px;
  line-height: 22px;
  margin: 0 0 12px;
}

.cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 4px;
}

.fixed-cards {
  margin-bottom: 12px;
}

.card {
  flex: 1;
  min-width: 140px;
}

.num {
  font-size: 22px;
  font-weight: 600;
  line-height: 32px;
}

.minus {
  color: #f56c6c;
}

.label {
  color: #606266;
  font-size: 13px;
}

.sub {
  color: #909399;
  font-size: 12px;
  margin-top: 4px;
}

.block-title {
  margin: 18px 0 8px;
  font-size: 15px;
  font-weight: 600;
}
</style>
